import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import { emitFinanceEvent } from "./finance/financeDispatcher";
import ApexoToothChart from "../careflow360/chart/ApexoToothChart";
import { useCareFlow360Store } from "../careflow360/hooks/useCareFlow360Store";
import { careflow360Api } from "../careflow360/api/careflow360Api";
import "./ClinicalBill.css";
import { exportElementToPdf } from "../../utils/pdfExport";
import { getVisualStateFromProc, type ToothVisualState } from "../shared/toothchart/toothOverlay";
import { subscribeCareflowSync } from "../../services/careflowSync";

interface Patient {
  patient_id: string | number;
  name: string;
}

interface Visit {
  visit_id?: number;
  status?: string;
}

interface Procedure {
  id: number;
  name: string;
  price?: number;
  cost?: number;
  requires_tooth?: boolean;
}

const REGULAR_KEYWORDS = [
  "consult",
  "check",
  "review",
  "follow",
  "scaling",
  "polish",
  "clean",
  "filling",
  "extraction",
  "rct",
  "root canal",
  "x-ray",
  "xray",
  "x ray",
  "dressing",
  "pulpotomy",
  "crown",
  "bridge",
];

const isRegularProcedure = (name: string): boolean => {
  const val = (name || "").toLowerCase();
  return REGULAR_KEYWORDS.some((key) => val.includes(key));
};

const sortProcedures = (list: Procedure[]): Procedure[] =>
  [...list].sort((a, b) => {
    const aRegular = isRegularProcedure(a.name);
    const bRegular = isRegularProcedure(b.name);
    if (aRegular !== bRegular) return aRegular ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

interface Consultant {
  id: number;
  name: string;
  specialization?: string;
  is_active?: boolean;
}

interface ConsultantService {
  id: number;
  name: string;
}

interface ConsultantFee {
  id: number;
  consultant_id: number;
  consultant_name?: string;
  service_id: number;
  service_name?: string;
  fee?: number;
}

interface Lab {
  id: number;
  name: string;
}

interface LabWork {
  id: number;
  name: string;
}

interface LabRate {
  id: number;
  lab_id: number;
  work_id: number;
  unit_cost: number;
}

interface DoctorProcedure {
  session_id?: number;
  procedure_id: number | null;
  name: string;
  teeth?: number[];
  base_price: number;
  consultant?: string | null;
  consultant_fee?: number;
  consultant_service_id?: number | null;
}

interface BillingProcedure {
  session_id?: number;
  procedure_id: number | null;
  name: string;
  teeth?: number[];
  base_price: number;
  final_price: number;
  consultant?: string | null;
  consultant_fee?: number;
  consultant_service_id?: number | null;
}

interface ClinicalBillingState {
  patient_id: string | number | null;
  visit_id: number | null;
  procedures: BillingProcedure[];
  show_discount: boolean;
}

interface ClinicalBillProps {
  patient: Patient | null;
  visit: Visit | null;
  onCreated?: (res: { id: number; paid?: boolean }) => void;
}

const normalizeProcedureName = (name: string) => name.trim().toLowerCase();
const normalizeKey = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "");
const mapCrownMaterial = (name: string): "metal" | "ceramic_normal" | "ceramic_premium" | "zirconia" | null => {
  const normalized = normalizeProcedureName(name);
  if (!normalized.includes("crown")) return null;
  if (normalized.includes("zirconia")) return "zirconia";
  if (normalized.includes("metal")) return "metal";
  if (normalized.includes("ceramic") || normalized.includes("porcelain")) {
    if (normalized.includes("premium") || normalized.includes("high")) return "ceramic_premium";
    return "ceramic_normal";
  }
  return null;
};

const findOverrideMaterial = (name: string, materialMap: Record<string, number>): string | null => {
  const key = normalizeKey(name);
  if (!key) return null;
  return Object.keys(materialMap).find((m) => {
    const mk = normalizeKey(m);
    return mk && key.includes(mk);
  }) || null;
};

const clampPrice = (value: number, max: number) => Math.max(0, Math.min(value, max));

type ToothchartMode = "auto" | "manual";

const CHART_STATE_PRIORITY: Record<ToothVisualState, number> = {
  Normal: 0,
  Filling: 1,
  RCT_Open: 2,
  RCT_Done: 3,
  Crown: 4,
  Veneer: 5,
  Implant: 6,
  Missing: 7,
};

const pickHigherChartState = (current: ToothVisualState, incoming: ToothVisualState): ToothVisualState => {
  return CHART_STATE_PRIORITY[incoming] >= CHART_STATE_PRIORITY[current] ? incoming : current;
};

const mapDoctorProceduresToBilling = (
  doctorList: DoctorProcedure[],
  crownMap: Record<string, number>
): BillingProcedure[] => doctorList.map((item) => {
  const teethInput = item.teeth && item.teeth.length > 0 ? item.teeth : [];
  const teeth = teethInput.map(t => Number(t)).filter(t => !isNaN(t));

  const crownMaterial = mapCrownMaterial(item.name);
  const overrideMaterial = findOverrideMaterial(item.name, crownMap);
  const override = crownMaterial ? Number(crownMap[crownMaterial] || 0) : overrideMaterial ? Number(crownMap[overrideMaterial] || 0) : 0;

  const multiplier = teeth.length > 0 ? teeth.length : 1;
  const basePrice = override > 0 ? override * multiplier : Number(item.base_price || 0);
  const rawFee = Number(item.consultant_fee || 0);

  return {
    session_id: item.session_id,
    procedure_id: item.procedure_id ?? null,
    name: item.name,
    teeth: teeth.length > 0 ? teeth : undefined,
    base_price: basePrice,
    final_price: basePrice,
    consultant: item.consultant ?? null,
    consultant_fee: rawFee * multiplier,
    consultant_service_id: item.consultant_service_id ?? null,
  };
});

const doctorProceduresSignature = (doctorList: DoctorProcedure[]): string => JSON.stringify(
  doctorList.map((item) => ({
    session_id: item.session_id,
    procedure_id: item.procedure_id ?? null,
    name: item.name ?? "",
    teeth: (item.teeth ?? [])
      .map((t) => Number(t))
      .filter((t) => Number.isFinite(t))
      .sort((a, b) => a - b),
    base_price: Number(item.base_price || 0),
    consultant: item.consultant ?? null,
  }))
);

export default function ClinicalBill({ patient, visit, onCreated }: ClinicalBillProps) {
  const [masterProcedures, setMasterProcedures] = useState<Procedure[]>([]);
  const [doctorProcedures, setDoctorProcedures] = useState<DoctorProcedure[]>([]);
  const [crownPrices, setCrownPrices] = useState<Record<string, number>>({});
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [consultantServices, setConsultantServices] = useState<ConsultantService[]>([]);
  const [consultantFees, setConsultantFees] = useState<ConsultantFee[]>([]);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [labWorks, setLabWorks] = useState<LabWork[]>([]);
  const [labRates, setLabRates] = useState<LabRate[]>([]);
  const [billing, setBilling] = useState<ClinicalBillingState>({
    patient_id: patient?.patient_id ?? null,
    visit_id: visit?.visit_id ?? null,
    procedures: [],
    show_discount: true
  });
  const [selectedProcName, setSelectedProcName] = useState("");
  const [otherMaterial, setOtherMaterial] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedBillId, setSavedBillId] = useState<number | null>(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [showChart, setShowChart] = useState(true);
  const [toothchartMode, setToothchartMode] = useState<ToothchartMode>("auto");
  const { selectedTeeth, clearSelection, setEvents, setVisitVersion } = useCareFlow360Store();
  const exportRef = useRef<HTMLDivElement>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [manualConsultantId, setManualConsultantId] = useState("");
  const [manualServiceId, setManualServiceId] = useState("");
  const [manualTeethInput, setManualTeethInput] = useState("");
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const doctorProceduresSigRef = useRef<string>("");

  const patientId = patient?.patient_id != null ? String(patient.patient_id) : "";

  const [consultantAudit, setConsultantAudit] = useState({
    enabled: false,
    consultantId: "",
    serviceId: "",
    fee: 0,
  });

  const [labAudit, setLabAudit] = useState({
    enabled: false,
    labId: "",
    workId: "",
    amount: 0,
  });

  const [paymentOption, setPaymentOption] = useState<"LATER" | "NOW">("LATER");
  const [cashPay, setCashPay] = useState("");
  const [upiPay, setUpiPay] = useState("");

  const normVisitStatus = (raw: unknown): string => String(raw ?? "").trim().toUpperCase().replace(/\s+/g, "_");
  const isLocked = Boolean(visit?.status && ["LOCKED", "CLOSED"].includes(normVisitStatus(visit.status)) || normVisitStatus(visit?.status) === "LOCKED" || normVisitStatus(visit?.status).includes("LOCK"));
  const inputsDisabled = reviewMode || Boolean(savedBillId) || loading || isLocked;
  const isManualMode = toothchartMode === "manual";

  useEffect(() => {
    const unsubscribe = subscribeCareflowSync((payload) => {
      if (!visit?.visit_id || payload.visitId == null) return;
      if (Number(payload.visitId) !== Number(visit.visit_id)) return;
      setRefreshKey((k) => k + 1);
    });
    return () => unsubscribe();
  }, [visit?.visit_id]);

  useEffect(() => {
    if (showChart && visit?.visit_id && patientId) {
      careflow360Api.fetchChartEvents(visit.visit_id, patientId).then((res: { events: any[]; visitVersion: number }) => {
        setEvents(res.events);
        setVisitVersion(res.visitVersion);
      }).catch(console.error);
    }
  }, [showChart, visit?.visit_id, patientId, setEvents, setVisitVersion]);

  useEffect(() => {
    const loadData = async () => {
      if (!patientId) {
        setBilling({
          patient_id: patient?.patient_id ?? null,
          visit_id: visit?.visit_id ?? null,
          procedures: [],
          show_discount: true
        });
        setDoctorProcedures([]);
        setMasterProcedures([]);
        return;
      }

      setLoading(true);
      try {
        const crownRes = await apiGet("/api/manager/crown-material-prices");
        const crownList = Array.isArray(crownRes) ? (crownRes as { material: string; unit_cost: number }[]) : [];
        const crownMap: Record<string, number> = {};
        crownList.forEach((row) => {
          crownMap[row.material] = Number(row.unit_cost || 0);
        });
        setCrownPrices(crownMap);

        let procedureRes: unknown = null;
        try {
          procedureRes = await apiGet("/manager/billable-procedures");
        } catch {
          procedureRes = null;
        }
        let masters = Array.isArray(procedureRes) ? (procedureRes as Procedure[]) : [];
        if (masters.length === 0) {
          try {
            const fallbackRes = await apiGet("/api/manager/billable-procedures");
            masters = Array.isArray(fallbackRes) ? (fallbackRes as Procedure[]) : [];
          } catch {
            masters = [];
          }
        }
        setMasterProcedures(sortProcedures(masters));

        // Build API URL with optional visit_id
        const apiUrl = visit?.visit_id
          ? `/manager/clinical/procedures?patient_id=${encodeURIComponent(patientId)}&visit_id=${visit.visit_id}`
          : `/manager/clinical/procedures?patient_id=${encodeURIComponent(patientId)}`;

        const doctorRes = await apiGet(apiUrl);

        const doctorList = Array.isArray(doctorRes) ? (doctorRes as DoctorProcedure[]) : [];
        doctorProceduresSigRef.current = doctorProceduresSignature(doctorList);
        setDoctorProcedures(doctorList);
        setLastSynced(new Date());

        // Reset audit state on NEW load to allow re-sync
        setConsultantAudit({ enabled: false, consultantId: "", serviceId: "", fee: 0 });
        setLabAudit({ enabled: false, labId: "", workId: "", amount: 0 });

        if (toothchartMode === "auto") {
          const mapped = mapDoctorProceduresToBilling(doctorList, crownMap);
          setBilling({
            patient_id: patient?.patient_id ?? null,
            visit_id: visit?.visit_id ?? null,
            procedures: mapped,
            show_discount: true
          });
        }
      } catch (e) {
        console.error("Failed to load billing data", e);
        setBilling({
          patient_id: patient?.patient_id ?? null,
          visit_id: visit?.visit_id ?? null,
          procedures: [],
          show_discount: true
        });
        setDoctorProcedures([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [patient?.patient_id, patientId, visit?.visit_id, refreshKey, toothchartMode]);

  useEffect(() => {
    doctorProceduresSigRef.current = doctorProceduresSignature(doctorProcedures);
  }, [doctorProcedures]);

  useEffect(() => {
    if (!patientId) return;
    if (toothchartMode !== "auto") return;
    if (savedBillId) return;

    let active = true;
    const syncDoctorData = async () => {
      try {
        // Build API URL with optional visit_id
        const apiUrl = visit?.visit_id
          ? `/manager/clinical/procedures?patient_id=${encodeURIComponent(patientId)}&visit_id=${visit.visit_id}`
          : `/manager/clinical/procedures?patient_id=${encodeURIComponent(patientId)}`;

        const doctorRes = await apiGet(apiUrl);
        const doctorList = Array.isArray(doctorRes) ? (doctorRes as DoctorProcedure[]) : [];
        const nextSig = doctorProceduresSignature(doctorList);
        if (!active || nextSig === doctorProceduresSigRef.current) return;

        doctorProceduresSigRef.current = nextSig;
        setDoctorProcedures(doctorList);
        setLastSynced(new Date());
        setBilling((prev) => ({
          ...prev,
          patient_id: patient?.patient_id ?? null,
          visit_id: visit?.visit_id ?? null,
          procedures: mapDoctorProceduresToBilling(doctorList, crownPrices),
        }));
      } catch (e) {
        console.warn("Auto-sync failed for clinical procedures", e);
      }
    };

    const syncOnFocus = () => {
      void syncDoctorData();
    };
    const syncOnVisibility = () => {
      if (document.visibilityState === "visible") {
        void syncDoctorData();
      }
    };

    const timer = window.setInterval(() => {
      void syncDoctorData();
    }, 5000);
    window.addEventListener("focus", syncOnFocus);
    document.addEventListener("visibilitychange", syncOnVisibility);

    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", syncOnFocus);
      document.removeEventListener("visibilitychange", syncOnVisibility);
    };
  }, [patientId, visit?.visit_id, toothchartMode, savedBillId, patient?.patient_id, crownPrices]);

  useEffect(() => {
    if (!patientId) return;
    if (!visit?.visit_id) return;
    if (toothchartMode !== "auto") return;
    if (doctorProcedures.length === 0) return;
    setBilling((prev) => ({
      ...prev,
      patient_id: patient?.patient_id ?? null,
      visit_id: visit?.visit_id ?? null,
      procedures: mapDoctorProceduresToBilling(doctorProcedures, crownPrices)
    }));
  }, [toothchartMode, doctorProcedures, crownPrices, patient?.patient_id, visit?.visit_id, patientId]);

  useEffect(() => {
    if (!patientId) return;
    if (!isManualMode) return;
    const nextPatientId = patient?.patient_id ?? null;
    const nextVisitId = visit?.visit_id ?? null;
    if (billing.patient_id === nextPatientId && billing.visit_id === nextVisitId) return;
    setBilling({
      patient_id: nextPatientId,
      visit_id: nextVisitId,
      procedures: [],
      show_discount: true
    });
    clearSelection();
  }, [patientId, visit?.visit_id, isManualMode, patient?.patient_id, billing.patient_id, billing.visit_id]);

  useEffect(() => {
    const loadAuditMasters = async () => {
      try {
        const [consultantRes, serviceRes, feeRes, labRes, workRes, rateRes] = await Promise.all([
          apiGet("/api/admin/consultants"),
          apiGet("/api/manager/consultant-services"),
          apiGet("/api/manager/consultant-service-fees"),
          apiGet("/api/manager/labs"),
          apiGet("/api/manager/lab-works"),
          apiGet("/api/manager/lab-rates"),
        ]);
        const consultantList = Array.isArray(consultantRes) ? (consultantRes as Consultant[]) : [];
        const loadedConsultants = consultantList.filter((c) => c.is_active !== false);
        setConsultants(loadedConsultants);
        const loadedServices = Array.isArray(serviceRes) ? (serviceRes as ConsultantService[]) : [];
        setConsultantServices(loadedServices);
        setConsultantFees(Array.isArray(feeRes) ? (feeRes as ConsultantFee[]) : []);
        setLabs(Array.isArray(labRes) ? (labRes as Lab[]) : []);
        setLabWorks(Array.isArray(workRes) ? (workRes as LabWork[]) : []);
        setLabRates(Array.isArray(rateRes) ? (rateRes as LabRate[]) : []);
      } catch (e) {
        console.warn("Failed to load audit masters", e);
      }
    };
    loadAuditMasters();
  }, []);

  useEffect(() => {
    if (!consultantAudit.enabled) return;
    const consultantId = Number(consultantAudit.consultantId);
    const serviceId = Number(consultantAudit.serviceId);
    if (!consultantId || !serviceId) return;
    const feeRow = consultantFees.find((f) => f.consultant_id === consultantId && f.service_id === serviceId);
    if (!feeRow) return;
    setConsultantAudit((prev) => ({ ...prev, fee: Number(feeRow.fee || 0) }));
  }, [consultantAudit.consultantId, consultantAudit.serviceId, consultantAudit.enabled, consultantFees]);

  useEffect(() => {
    if (!labAudit.enabled) return;
    const labId = Number(labAudit.labId);
    const workId = Number(labAudit.workId);
    if (!labId || !workId) return;
    const rateRow = labRates.find((r) => r.lab_id === labId && r.work_id === workId);
    if (!rateRow) return;
    setLabAudit((prev) => ({ ...prev, amount: Number(rateRow.unit_cost || 0) }));
  }, [labAudit.labId, labAudit.workId, labAudit.enabled, labRates]);

  // Auto-sync Consultant Audit whenever doctorProcedures or masters change
  useEffect(() => {
    if (doctorProcedures.length === 0 || consultants.length === 0) {
      if (consultantAudit.enabled && !consultantAudit.consultantId && !consultantAudit.serviceId) {
        setConsultantAudit(prev => ({ ...prev, enabled: false, consultantId: "", serviceId: "", fee: 0 }));
      }
      return;
    }

    // Do not aggressively overwrite if user has manually selected a consultant or service
    if (consultantAudit.consultantId || consultantAudit.serviceId) {
      return;
    }

    // Suggest if auto-match found
    const procWithC = doctorProcedures.find(p => p.consultant);
    if (!procWithC) return;

    const cName = normalizeProcedureName(procWithC.consultant || "");
    const foundC = consultants.find(c => normalizeProcedureName(c.name) === cName || normalizeKey(c.name) === normalizeKey(cName));

    let foundS: ConsultantService | undefined;
    if (procWithC.consultant_service_id) {
      foundS = consultantServices.find(s => s.id === procWithC.consultant_service_id);
    }

    if (!foundS) {
      const pName = normalizeProcedureName(procWithC.name || "");
      foundS = consultantServices.find(s => {
        const sName = normalizeProcedureName(s.name);
        return pName.includes(sName) || sName.includes(pName) || normalizeKey(s.name).includes(normalizeKey(pName));
      });
    }

    if (foundC || foundS) {
      const nextCId = foundC ? String(foundC.id) : consultantAudit.consultantId;
      const nextSId = foundS ? String(foundS.id) : consultantAudit.serviceId;

      // Only update if state actually changes to avoid cycles
      if (nextCId !== consultantAudit.consultantId || nextSId !== consultantAudit.serviceId || !consultantAudit.enabled) {
        setConsultantAudit(prev => ({
          ...prev,
          enabled: true,
          consultantId: nextCId,
          serviceId: nextSId
        }));
      }
    }
  }, [doctorProcedures, consultants, consultantServices, consultantAudit.consultantId, consultantAudit.serviceId, consultantAudit.enabled]);

  const suggestConsultantAudit = () => {
    const candidates = [
      ...(selectedProcName ? [selectedProcName] : []),
      ...billing.procedures.map((p) => p.name),
      ...doctorProcedures.map((p) => p.name),
    ].filter(Boolean);

    const normalizedCandidates = candidates.map((c) => normalizeProcedureName(c));

    let bestService: ConsultantService | null = null;
    let bestScore = 0;
    consultantServices.forEach((service) => {
      const serviceKey = normalizeProcedureName(service.name);
      if (!serviceKey) return;
      normalizedCandidates.forEach((cand) => {
        if (cand.includes(serviceKey) || serviceKey.includes(cand)) {
          const score = Math.max(serviceKey.length, cand.length);
          if (score > bestScore) {
            bestScore = score;
            bestService = service;
          }
        }
      });
    });

    let suggestedConsultant: Consultant | null = null;
    const doctorMatch = bestService
      ? doctorProcedures.find((p) => {
        const serviceKey = normalizeProcedureName(bestService?.name || "");
        const procKey = normalizeProcedureName(p.name);
        return Boolean(serviceKey) && (procKey.includes(serviceKey) || serviceKey.includes(procKey));
      })
      : doctorProcedures.find((p) => Boolean(p.consultant));
    if (doctorMatch?.consultant) {
      const targetKey = normalizeProcedureName(doctorMatch.consultant);
      suggestedConsultant = consultants.find((c) => normalizeProcedureName(c.name) === targetKey)
        || consultants.find((c) => normalizeProcedureName(c.name).includes(targetKey))
        || null;
    }

    if (!bestService && !suggestedConsultant) {
      alert("No consultant/service match found for auto-suggest.");
      return;
    }

    setConsultantAudit((prev) => ({
      ...prev,
      enabled: true,
      consultantId: suggestedConsultant?.id ? String(suggestedConsultant.id) : prev.consultantId,
      serviceId: bestService?.id ? String(bestService.id) : prev.serviceId,
    }));
  };

  const manualChartStates = useMemo<Record<number, ToothVisualState>>(() => {
    const stateMap: Record<number, ToothVisualState> = {};
    if (!isManualMode) return stateMap;
    billing.procedures.forEach((proc) => {
      if (!proc.teeth || proc.teeth.length === 0) return;
      const state = getVisualStateFromProc(proc.name);
      if (state === "Normal") return;
      proc.teeth.forEach((tooth: number) => {
        const current = stateMap[tooth] ?? "Normal";
        stateMap[tooth] = pickHigherChartState(current, state);
      });
    });

    const previewState = selectedProcName ? getVisualStateFromProc(selectedProcName) : "Normal";
    if (previewState !== "Normal" && selectedTeeth.length > 0) {
      selectedTeeth.forEach((tooth) => {
        const current = stateMap[tooth] ?? "Normal";
        stateMap[tooth] = pickHigherChartState(current, previewState);
      });
    }

    return stateMap;
  }, [billing.procedures, isManualMode, selectedProcName, selectedTeeth]);

  const autoChartStates = useMemo<Record<number, ToothVisualState>>(() => {
    const stateMap: Record<number, ToothVisualState> = {};
    if (toothchartMode !== "auto") return stateMap;
    doctorProcedures.forEach((proc) => {
      if (!proc.teeth || proc.teeth.length === 0) return;
      const state = getVisualStateFromProc(proc.name);
      if (state === "Normal") return;
      proc.teeth.forEach((tooth: number) => {
        const current = stateMap[tooth] ?? "Normal";
        stateMap[tooth] = pickHigherChartState(current, state);
      });
    });
    return stateMap;
  }, [doctorProcedures, toothchartMode]);

  const addProcedure = () => {
    if (!selectedProcName || inputsDisabled) return;
    if (!isManualMode) return;
    const normalizedName = normalizeProcedureName(selectedProcName);
    const master =
      masterProcedures.find((p) => normalizeProcedureName(p.name) === normalizedName) ||
      masterProcedures.find((p) => normalizeProcedureName(p.name).includes(normalizedName)) ||
      masterProcedures.find((p) => normalizedName.includes(normalizeProcedureName(p.name)));
    if (!master) return;

    let teeth: number[] = [];
    if (manualTeethInput.trim()) {
      teeth = manualTeethInput.split(/[,\\s]+/).map(t => Number(t.trim())).filter(t => !isNaN(t) && t > 0);
    } else if (selectedTeeth.length > 0) {
      teeth = [...selectedTeeth];
    }

    if (master.requires_tooth && teeth.length === 0) {
      alert("This procedure requires teeth. Select teeth before adding.");
      return;
    }

    const crownMaterial = mapCrownMaterial(master.name);
    const overrideMaterial = findOverrideMaterial(master.name, crownPrices);
    const override = crownMaterial ? Number(crownPrices[crownMaterial] || 0) : overrideMaterial ? Number(crownPrices[overrideMaterial] || 0) : 0;
    const unitPrice = override > 0 ? override : Number(master.price ?? master.cost ?? 0);
    const multiplier = teeth.length > 0 ? teeth.length : 1;
    const basePrice = unitPrice * multiplier;

    setBilling((prev) => ({
      ...prev,
      procedures: [
        ...prev.procedures,
        {
          procedure_id: master.id,
          name: master.name,
          teeth: teeth.length > 0 ? teeth : undefined,
          base_price: basePrice,
          final_price: basePrice,
          consultant: consultants.find(c => String(c.id) === manualConsultantId)?.name || null,
          consultant_service_id: manualServiceId ? Number(manualServiceId) : null,
          consultant_fee: (consultantFees.find(f => String(f.consultant_id) === manualConsultantId && String(f.service_id) === manualServiceId)?.fee || 0) * multiplier
        }
      ]
    }));

    setSelectedProcName("");
    clearSelection();
    setManualTeethInput("");
    setManualConsultantId("");
    setManualServiceId("");
  };

  const addOtherMaterialProcedure = () => {
    if (!otherMaterial || inputsDisabled) return;
    if (!isManualMode) return;
    const teeth = selectedTeeth.length > 0 ? [...selectedTeeth] : [];
    if (teeth.length === 0) {
      alert("Select teeth for other material.");
      return;
    }
    const unitPrice = Number(crownPrices[otherMaterial] || 0);
    const basePrice = unitPrice * teeth.length;
    setBilling((prev) => ({
      ...prev,
      procedures: [
        ...prev.procedures,
        {
          procedure_id: null,
          name: `Crown - ${otherMaterial}`,
          teeth,
          base_price: basePrice,
          final_price: basePrice
        }
      ]
    }));
    setOtherMaterial("");
    clearSelection();
  };

  const updateFinalPrice = (index: number, value: number) => {
    if (inputsDisabled) return;
    setBilling((prev) => {
      const next = [...prev.procedures];
      const current = next[index];
      if (!current) return prev;
      next[index] = {
        ...current,
        final_price: clampPrice(value, current.base_price)
      };
      return { ...prev, procedures: next };
    });
  };

  const removeProcedure = (index: number) => {
    if (inputsDisabled) return;
    setBilling((prev) => ({
      ...prev,
      procedures: prev.procedures.filter((_, i) => i !== index)
    }));
  };

  const baseTotal = billing.procedures.reduce((sum, p) => sum + p.base_price, 0);
  const finalTotal = billing.procedures.reduce((sum, p) => sum + p.final_price, 0);
  const discountTotal = Math.max(0, baseTotal - finalTotal);
  const internalAuditTotal =
    (consultantAudit.enabled ? Number(consultantAudit.fee || 0) : 0) +
    (labAudit.enabled ? Number(labAudit.amount || 0) : 0) +
    billing.procedures.reduce((sum, p) => sum + Number(p.consultant_fee || 0), 0);

  const formatError = (e: unknown): string => {
    if (e && typeof e === "object") {
      const err = e as Error & { status?: number; body?: unknown };
      const status = err.status ? ` (HTTP ${err.status})` : "";
      const msg = err.message || "Unknown error";
      return `${msg}${status}`;
    }
    return String(e ?? "Unknown error");
  };

  const handleSubmit = async () => {
    if (!patient) return alert("Patient missing");
    if (isLocked) return alert("Visit is locked. Billing disabled.");
    if (billing.procedures.length === 0) return alert("Add at least one procedure");

    setLoading(true);

    const selectedConsultant = consultants.find((c) => c.id === Number(consultantAudit.consultantId));
    const selectedService = consultantServices.find((s) => s.id === Number(consultantAudit.serviceId));
    const selectedLab = labs.find((l) => l.id === Number(labAudit.labId));
    const selectedWork = labWorks.find((w) => w.id === Number(labAudit.workId));

    // Collect tooth numbers for consultant audit
    const auditTeeth = Array.from(new Set(
      billing.procedures
        .filter(p => p.teeth && p.teeth.length > 0)
        .flatMap(p => p.teeth || [])
    )).join(",");

    const perProcedureConsultantTotal = billing.procedures.reduce((sum, p) => sum + Number(p.consultant_fee || 0), 0);
    const totalConsultantFees = (consultantAudit.enabled ? Number(consultantAudit.fee || 0) : 0) + perProcedureConsultantTotal;

    const payload = {
      patient_id: patientId,
      visit_id: visit?.visit_id ?? null,
      procedures: billing.procedures.map((p) => ({
        session_id: p.session_id,
        procedure_id: p.procedure_id,
        name: p.name,
        teeth: p.teeth ?? [],
        base_price: p.base_price,
        final_price: p.final_price,
        consultant_name: p.consultant ?? null,
        consultant_fee: Number(p.consultant_fee || 0),
        consultant_service_id: p.consultant_service_id ?? null,
      })),
      total_amount: baseTotal,
      final_amount: finalTotal,
      discount: discountTotal,
      audit: {
        consultant: {
          enabled: consultantAudit.enabled,
          consultant_id: consultantAudit.consultantId ? Number(consultantAudit.consultantId) : null,
          consultant_name: selectedConsultant?.name || null,
          service_id: consultantAudit.serviceId ? Number(consultantAudit.serviceId) : null,
          service_name: selectedService?.name || null,
          fee: consultantAudit.enabled ? Number(consultantAudit.fee || 0) : 0,
          tooth_no: auditTeeth || null
        },
        lab: {
          enabled: labAudit.enabled,
          lab_id: labAudit.labId ? Number(labAudit.labId) : null,
          lab_name: selectedLab?.name || null,
          work_id: labAudit.workId ? Number(labAudit.workId) : null,
          work_name: selectedWork?.name || null,
          amount: labAudit.enabled ? Number(labAudit.amount || 0) : 0,
        },
      },
    };

    try {
      const res = (await apiPost("/manager/clinical/bill/save", payload)) as { id: number };
      setSavedBillId(res.id);

      if (totalConsultantFees > 0) {
        await emitFinanceEvent({
          source: "CONSULTANT_FEE",
          ref_id: String(res.id),
          debit: 0,
          credit: totalConsultantFees,
          description: `Consultant fees (Bill #${res.id}) – Total: ₹${totalConsultantFees}`,
          meta: {
            bill_id: res.id,
            patient_id: patientId,
            consultant_id: consultantAudit.enabled ? (selectedConsultant?.id ?? null) : null,
          }
        });
      }

      if (labAudit.enabled && Number(labAudit.amount || 0) > 0) {
        await emitFinanceEvent({
          source: "LAB_INTERNAL",
          ref_id: String(res.id),
          debit: 0,
          credit: Number(labAudit.amount || 0),
          description: `Lab work – ${selectedLab?.name || "Lab"}`,
          meta: {
            bill_id: res.id,
            patient_id: patientId,
            lab_id: selectedLab?.id ?? null,
            work_id: selectedWork?.id ?? null,
          }
        });
      }

      // Handle Immediate Payment
      let isPaid = finalTotal <= 0.1;
      if (paymentOption === "NOW") {
        const c = Number(cashPay || 0);
        const u = Number(upiPay || 0);

        if (c > 0) {
          await apiPost("/api/manager/payment", { bill_id: res.id, amount: c, method: "Cash" });
        }
        if (u > 0) {
          await apiPost("/api/manager/payment", { bill_id: res.id, amount: u, method: "UPI" });
        }

        if (c + u >= finalTotal - 0.1) {
          isPaid = true;
        }

        // Emit Finance Event for Clinical Payment
        if (c + u > 0) {
          await emitFinanceEvent({
            source: "CLINICAL_PAYMENT",
            ref_id: String(res.id),
            debit: c + u,
            credit: 0,
            description: `Clinical payment (Bill #${res.id}) - ${patient?.name || "Patient"}`,
            meta: {
              bill_id: res.id,
              patient_id: patientId,
              method: c > 0 && u > 0 ? "Split" : c > 0 ? "Cash" : "UPI",
              cash_amount: c,
              upi_amount: u
            }
          });
        }
      }

      onCreated?.({ id: res.id, paid: isPaid });
    } catch (e) {
      console.error(e);
      alert(`Failed to create bill: ${formatError(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintBill = () => {
    if (!savedBillId) return;
    window.open(`/print?type=bill&bill=${savedBillId}`, "_blank");
  };

  const handleExportPdf = async () => {
    await exportElementToPdf(exportRef.current, { filename: `clinical-bill-${patient?.patient_id ?? "patient"}.pdf` });
  };

  const otherMaterials = Object.keys(crownPrices).filter(
    (m) => !["metal", "ceramic_normal", "ceramic_premium", "zirconia"].includes(m)
  );

  return (
    <div className="clinical-bill-container" ref={exportRef}>
      <header className="cb-header">
        <div className="cb-title">
          <h3>🩺 Clinical Billing</h3>
        </div>
        <div className="cb-meta">
          <div>
            <strong>Patient:</strong> {patient?.name ?? "—"} ({patient?.patient_id ?? "N/A"})
          </div>
          <div>
            <strong>Visit:</strong> {visit?.visit_id ? `#${visit.visit_id}` : "Counter"}
          </div>
          <div>
            <strong>Status:</strong> {isLocked ? "Locked" : savedBillId ? "Saved" : reviewMode ? "Review" : "Draft"}
          </div>
        </div>
        <div className="cb-header-actions" data-export-ignore="true">
          <div className="cb-mode-toggle">
            <button
              type="button"
              className={toothchartMode === "auto" ? "active" : ""}
              onClick={() => setToothchartMode("auto")}
              disabled={inputsDisabled}
            >
              Auto Mode
            </button>
            <button
              type="button"
              className={toothchartMode === "manual" ? "active" : ""}
              onClick={() => setToothchartMode("manual")}
              disabled={inputsDisabled}
            >
              Manual Mode
            </button>
          </div>
          <label className="toggle-row">
            <input
              type="checkbox"
              checked={showChart}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowChart(e.target.checked)}
            />
            Show Tooth Chart
          </label>
        </div>
      </header>

      <div className="clinical-grid">
        <aside className="clinical-left">
          <div className="panel-card">
            <div className="panel-header panel-header-row">
              <span>{isManualMode ? "Manual Tooth Selection" : "Treatment Teeth (Doctor Recorded)"}</span>
            </div>
            {showChart ? (
              isManualMode ? (
                <div className="manual-chart-wrapper">
                  <ApexoToothChart patientId={patientId} visitId={visit?.visit_id ?? null} />
                  <div className="manual-selection">
                    <div className="panel-subheader">Selected Teeth</div>
                    <div className="selected-teeth-list">
                      {selectedTeeth.length > 0 ? selectedTeeth.join(", ") : "None"}
                    </div>
                  </div>
                </div>
              ) : (
                <ApexoToothChart patientId={patientId} visitId={visit?.visit_id ?? null} />
              )
            ) : (
              <div className="muted">Tooth chart disabled.</div>
            )}
          </div>
        </aside>

        <section className="clinical-main">
          <div className="panel-card">
            <div className="panel-header panel-header-row">
              <span>Procedure List</span>
              <button
                type="button"
                className="btn-refresh"
                onClick={() => setRefreshKey((k) => k + 1)}
                disabled={loading}
                title="Refresh doctor procedures"
              >
                🔄 Refresh
              </button>
              {lastSynced && (
                <span className="last-sync-text" style={{ fontSize: "0.75rem", color: "#64748b", marginLeft: "8px" }}>
                  Last synced: {lastSynced.toLocaleTimeString()}
                </span>
              )}
            </div>
            <div className="table-wrapper">
              <table className="bill-table">
                <thead>
                  <tr>
                    <th style={{ width: "30%" }}>Procedure Name</th>
                    <th style={{ width: "15%" }}>Teeth</th>
                    <th style={{ width: "20%" }}>Consultant</th>
                    <th style={{ width: "12%" }}>Base Price</th>
                    <th style={{ width: "12%" }}>Final Price</th>
                    {billing.show_discount && (
                      <th style={{ width: "10%" }}>Discount</th>
                    )}
                    <th style={{ width: "5%" }} />
                  </tr>
                </thead>
                <tbody>
                  {billing.procedures.map((row, index) => {
                    const discount = Math.max(0, row.base_price - row.final_price);
                    return (
                      <tr key={`${row.procedure_id ?? "proc"}-${index}`}>
                        <td>{row.name}</td>
                        <td>{row.teeth && row.teeth.length > 0 ? row.teeth.join(", ") : "—"}</td>
                        <td>
                          <select
                            className="input-grid"
                            value={row.consultant_service_id ? `${row.consultant || ""}_${row.consultant_service_id}` : ""}
                            disabled={inputsDisabled}
                            onChange={(e) => {
                              const [cId, sId] = e.target.value.split("_");
                              setBilling(prev => {
                                const next = [...prev.procedures];
                                const current = next[index];
                                const cName = consultants.find(c => String(c.id) === cId)?.name || null;
                                const fee = consultantFees.find(f => String(f.consultant_id) === cId && String(f.service_id) === sId)?.fee || 0;
                                next[index] = {
                                  ...current,
                                  consultant: cName,
                                  consultant_service_id: sId ? Number(sId) : null,
                                  consultant_fee: fee * (current.teeth?.length || 1)
                                };
                                return { ...prev, procedures: next };
                              });
                            }}
                          >
                            <option value="">None</option>
                            {consultants.map(c => (
                              consultantServices.map(s => {
                                const fee = consultantFees.find(f => f.consultant_id === c.id && f.service_id === s.id);
                                if (!fee) return null;
                                return (
                                  <option key={`${c.id}_${s.id}`} value={`${c.id}_${s.id}`}>
                                    {c.name} - {s.name}
                                  </option>
                                );
                              })
                            ))}
                          </select>
                        </td>
                        <td className="muted">₹{row.base_price}</td>
                        <td>
                          <input
                            className="input-grid"
                            type="number"
                            min={0}
                            max={row.base_price}
                            value={row.final_price}
                            disabled={inputsDisabled}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateFinalPrice(index, Number(e.target.value) || 0)
                            }
                          />
                        </td>
                        {billing.show_discount && (
                          <td className="text-right">₹{discount}</td>
                        )}
                        <td>
                          <button
                            className="btn-icon-del"
                            type="button"
                            onClick={() => removeProcedure(index)}
                            title="Remove Item"
                            disabled={inputsDisabled}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {billing.procedures.length === 0 && (
                    <tr>
                      <td colSpan={billing.show_discount ? 6 : 5} className="muted">
                        No procedures loaded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="panel-subsection">
              <div className="panel-subheader">Add Procedure</div>
              {isManualMode ? (
                <>
                  <div className="add-procedure-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1.5fr) 100px 1fr 1fr auto', gap: '10px' }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="procedure-search-input"
                        list="clinical-procedure-list"
                        style={{ width: '100%' }}
                        value={selectedProcName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedProcName(e.target.value)}
                        placeholder="Search procedure..."
                        disabled={inputsDisabled}
                      />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px' }}
                        value={selectedTeeth.length > 0 && !manualTeethInput ? selectedTeeth.join(",") : manualTeethInput}
                        onChange={(e) => setManualTeethInput(e.target.value)}
                        placeholder="Teeth"
                        disabled={inputsDisabled}
                        title="Selected from chart or type manually (e.g. 16,17)"
                      />
                    </div>
                    <select
                      className="manual-consultant-select"
                      style={{ width: '100%' }}
                      value={manualConsultantId}
                      onChange={(e) => setManualConsultantId(e.target.value)}
                      disabled={inputsDisabled}
                    >
                      <option value="">Select Consultant</option>
                      {consultants.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <select
                      className="manual-service-select"
                      style={{ width: '100%' }}
                      value={manualServiceId}
                      onChange={(e) => setManualServiceId(e.target.value)}
                      disabled={inputsDisabled || !manualConsultantId}
                    >
                      <option value="">Select Service</option>
                      {consultantServices.map(s => {
                        const fee = consultantFees.find(f => String(f.consultant_id) === manualConsultantId && f.service_id === s.id);
                        if (!fee && manualConsultantId) return null;
                        return <option key={s.id} value={s.id}>{s.name} (₹{fee?.fee || 0})</option>;
                      })}
                    </select>
                    <button className="btn-action" type="button" onClick={addProcedure} disabled={inputsDisabled} style={{ padding: '0 20px' }}>
                      + Add
                    </button>
                  </div>
                  <datalist id="clinical-procedure-list">
                    {masterProcedures.map((p) => (
                      <option key={p.id} value={p.name} />
                    ))}
                  </datalist>
                  {otherMaterials.length > 0 && (
                    <div className="other-materials">
                      <div className="panel-subheader">Other Crown Materials</div>
                      <div className="other-materials-list">
                        {otherMaterials.map((m) => (
                          <span key={m} className="other-material-pill">
                            {m} — ₹{Number(crownPrices[m] || 0).toLocaleString("en-IN")}
                          </span>
                        ))}
                      </div>
                      <div className="other-materials-actions">
                        <select
                          value={otherMaterial}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOtherMaterial(e.target.value)}
                          disabled={inputsDisabled}
                        >
                          <option value="">Select material</option>
                          {otherMaterials.map((m) => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                        <button
                          className="btn-action"
                          type="button"
                          onClick={addOtherMaterialProcedure}
                          disabled={inputsDisabled}
                        >
                          + Add Material
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="muted">
                  Auto mode uses doctor-recorded procedures only. Switch to Manual Mode to add items.
                </div>
              )}
            </div>
          </div>

          <div className="panel-card summary-card">
            <div className="panel-header">Price Summary</div>
            <div className="summary-row">
              <span>Base Total:</span>
              <strong>₹{baseTotal}</strong>
            </div>
            {billing.show_discount && (
              <div className="summary-row">
                <span>Discount:</span>
                <strong>₹{discountTotal}</strong>
              </div>
            )}
            <div className="summary-row grand">
              <span>Final Total:</span>
              <span>₹{finalTotal}</span>
            </div>
            <label className="spy-toggle" style={{ marginTop: 12 }}>
              <input
                type="checkbox"
                checked={billing.show_discount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setBilling((prev) => ({ ...prev, show_discount: e.target.checked }))
                }
                disabled={inputsDisabled}
              />
              <span>Show Discount</span>
            </label>

            <div className="payment-quick-actions" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #eee' }}>
              <div className="flex gap-2 mb-3">
                <button
                  className={`btn-sm ${paymentOption === "LATER" ? "active-dark" : "btn-outline"}`}
                  onClick={() => setPaymentOption("LATER")}
                  disabled={inputsDisabled}
                >
                  Pay Later
                </button>
                <button
                  className={`btn-sm ${paymentOption === "NOW" ? "active-success" : "btn-outline"}`}
                  onClick={() => {
                    setPaymentOption("NOW");
                    setCashPay(String(finalTotal));
                    setUpiPay("");
                  }}
                  disabled={inputsDisabled}
                >
                  Pay Now
                </button>
              </div>

              {paymentOption === "NOW" && (
                <div className="split-pay-box animate-fade-in">
                  <div className="flex gap-2 mb-2">
                    <div className="flex-1">
                      <label className="text-xs font-bold text-gray-500">Cash</label>
                      <input type="number" className="input-sm w-full" value={cashPay} onChange={e => setCashPay(e.target.value)} placeholder="0" />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-bold text-gray-500">UPI</label>
                      <input type="number" className="input-sm w-full" value={upiPay} onChange={e => setUpiPay(e.target.value)} placeholder="0" />
                    </div>
                  </div>
                  <div className="text-xs text-right font-bold text-gray-400">
                    Total: ₹{Number(cashPay || 0) + Number(upiPay || 0)} / ₹{finalTotal}
                  </div>
                </div>
              )}
            </div>

            <div className="summary-actions" style={{ marginTop: 20 }}>
              <button
                className="btn-action w-full"
                style={{ background: paymentOption === 'NOW' ? '#10b981' : '#2563eb', width: '100%' }}
                type="button"
                onClick={handleSubmit}
                disabled={loading || isLocked || savedBillId !== null}
              >
                {loading ? "Processing..." : paymentOption === "NOW" ? "Save & Record Payment" : "Save Bill (Pay Later)"}
              </button>
            </div>
          </div>

          {/* -- AUDIT CARD kept in clinical-main -- */}
          <div className="panel-card audit-card" data-export-ignore="true">
            <div className="panel-header">Internal Audit (Not on Patient Bill)</div>
            <div className="audit-actions">
              <button
                type="button"
                className="btn-action secondary"
                onClick={suggestConsultantAudit}
                disabled={inputsDisabled || consultantServices.length === 0}
              >
                Auto-suggest consultant & service
              </button>
            </div>
            <div className="audit-toggle-row">
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={consultantAudit.enabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setConsultantAudit((prev) => ({ ...prev, enabled: e.target.checked }))
                  }
                  disabled={inputsDisabled}
                />
                Procedure done by Consultant
              </label>
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={labAudit.enabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setLabAudit((prev) => ({ ...prev, enabled: e.target.checked }))
                  }
                  disabled={inputsDisabled}
                />
                Lab involved
              </label>
            </div>

            {consultantAudit.enabled && (
              <div className="audit-grid">
                <select
                  value={consultantAudit.consultantId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setConsultantAudit((prev) => ({ ...prev, consultantId: e.target.value }))
                  }
                  disabled={inputsDisabled}
                >
                  <option value="">Select consultant</option>
                  {consultants.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.specialization ? ` (${c.specialization})` : ""}
                    </option>
                  ))}
                </select>
                <select
                  value={consultantAudit.serviceId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setConsultantAudit((prev) => ({ ...prev, serviceId: e.target.value }))
                  }
                  disabled={inputsDisabled}
                >
                  <option value="">Select service</option>
                  {consultantServices.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min={0}
                  value={consultantAudit.fee}
                  disabled
                />
              </div>
            )}

            {labAudit.enabled && (
              <div className="audit-grid">
                <select
                  value={labAudit.labId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setLabAudit((prev) => ({ ...prev, labId: e.target.value }))
                  }
                  disabled={inputsDisabled}
                >
                  <option value="">Select lab</option>
                  {labs.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
                <select
                  value={labAudit.workId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setLabAudit((prev) => ({ ...prev, workId: e.target.value }))
                  }
                  disabled={inputsDisabled}
                >
                  <option value="">Select work</option>
                  {labWorks.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min={0}
                  value={labAudit.amount}
                  disabled
                />
              </div>
            )}

            <div className="summary-row audit-total">
              <span>Internal Audit Total:</span>
              <strong>₹{internalAuditTotal}</strong>
            </div>
          </div>
        </section>

        {/* ─── STICKY SUMMARY RAIL (3rd column) ─── */}
        {(() => {
          const cashPaid = Number(cashPay || 0);
          const upiPaid = Number(upiPay || 0);
          const totalPaid = cashPaid + upiPaid;
          const remaining = finalTotal - totalPaid;
          const isOver = paymentOption === "NOW" && totalPaid > finalTotal + 0.01;
          const isExact = paymentOption === "NOW" && Math.abs(remaining) < 0.01;

          // Inline validation
          const validationItems: Array<{ level: "error" | "warning" | "ok"; msg: string }> = [];
          if (isLocked) validationItems.push({ level: "error", msg: "Visit is locked – billing disabled" });
          else if (savedBillId) validationItems.push({ level: "ok", msg: `Bill #${savedBillId} saved` });
          else {
            if (billing.procedures.length === 0) validationItems.push({ level: "error", msg: "Add at least one procedure" });
            if (finalTotal === 0 && billing.procedures.length > 0) validationItems.push({ level: "warning", msg: "All items at ₹0 – intentional?" });
            if (paymentOption === "NOW" && isOver) validationItems.push({ level: "warning", msg: `Over-payment by ₹${(totalPaid - finalTotal).toFixed(0)}` });
            if (paymentOption === "NOW" && remaining > 0 && !isExact) validationItems.push({ level: "warning", msg: `₹${remaining.toFixed(0)} still unallocated` });
            if (billing.procedures.length > 0 && !isLocked && !savedBillId) validationItems.push({ level: "ok", msg: "Ready to save" });
          }
          const canSubmit = !loading && !isLocked && !savedBillId && billing.procedures.length > 0;

          return (
            <aside className="cb-rail" aria-label="Billing summary">
              {/* Summary totals */}
              <div className="cb-rail-card">
                <div className="cb-rail-title">Bill Summary</div>

                <div className="cb-rail-row">
                  <span>Subtotal</span>
                  <span className="cb-rail-val">₹{baseTotal.toLocaleString("en-IN")}</span>
                </div>

                {discountTotal > 0 && (
                  <div className="cb-rail-row discount">
                    <span>Discount</span>
                    <span className="cb-rail-val">−₹{discountTotal.toLocaleString("en-IN")}</span>
                  </div>
                )}

                {internalAuditTotal > 0 && (
                  <div className="cb-rail-row">
                    <span>Internal (audit)</span>
                    <span className="cb-rail-val" style={{ color: "rgba(255,255,255,0.3)" }}>₹{internalAuditTotal.toLocaleString("en-IN")}</span>
                  </div>
                )}

                <div className="cb-rail-row total">
                  <span>Due</span>
                  <span>₹{finalTotal.toLocaleString("en-IN")}</span>
                </div>
              </div>

              {/* Payment mode */}
              <div className="cb-rail-card">
                <div className="cb-rail-title">Payment</div>

                {!inputsDisabled && (
                  <div className="cb-pay-toggle">
                    <button
                      type="button"
                      className={`cb-pay-btn cb-pay-later${paymentOption === "LATER" ? " active" : ""}`}
                      onClick={() => setPaymentOption("LATER")}
                    >
                      Pay Later
                    </button>
                    <button
                      type="button"
                      className={`cb-pay-btn cb-pay-now${paymentOption === "NOW" ? " active" : ""}`}
                      onClick={() => { setPaymentOption("NOW"); setCashPay(String(finalTotal)); setUpiPay(""); }}
                    >
                      Pay Now
                    </button>
                  </div>
                )}

                {paymentOption === "NOW" && (
                  <>
                    <div className="cb-split-grid">
                      <div>
                        <div className="cb-split-label">Cash (₹)</div>
                        <input
                          type="number"
                          className="cb-split-input"
                          value={cashPay}
                          onChange={(e) => setCashPay(e.target.value)}
                          placeholder="0"
                          disabled={inputsDisabled}
                          min={0}
                        />
                      </div>
                      <div>
                        <div className="cb-split-label">UPI (₹)</div>
                        <input
                          type="number"
                          className="cb-split-input"
                          value={upiPay}
                          onChange={(e) => setUpiPay(e.target.value)}
                          placeholder="0"
                          disabled={inputsDisabled}
                          min={0}
                        />
                      </div>
                    </div>
                    <div className={`cb-split-remaining${isOver ? " over" : isExact ? " exact" : ""}`}>
                      {isExact ? "✓ Fully paid" : isOver ? `⚠ Over by ₹${(totalPaid - finalTotal).toFixed(0)}` : `Remaining: ₹${remaining.toFixed(0)}`}
                    </div>
                  </>
                )}

                {/* Inline validation */}
                {validationItems.length > 0 && (
                  <div className="cb-validation" role="status" aria-live="polite">
                    {validationItems.map((v, i) => (
                      <div key={i} className={`cb-val-item ${v.level}`}>
                        <span>{v.level === "error" ? "✕" : v.level === "warning" ? "⚠" : "✓"}</span>
                        <span>{v.msg}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="button"
                  className={`cb-rail-submit${paymentOption === "NOW" ? " pay-now" : ""}`}
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  aria-label={paymentOption === "NOW" ? "Save bill and record payment" : "Save bill for payment later"}
                >
                  {loading ? "Processing…" : paymentOption === "NOW" ? "Save & Record Payment" : "Save Bill (Pay Later)"}
                </button>
              </div>

              {/* Print/Export */}
              <div className="cb-rail-card" style={{ gap: 8 }} data-export-ignore="true">
                <button
                  type="button"
                  className="btn-action secondary"
                  style={{ width: "100%", fontSize: "0.8rem" }}
                  onClick={handleExportPdf}
                >
                  Export PDF
                </button>
                <button
                  type="button"
                  className="btn-action"
                  style={{ width: "100%", fontSize: "0.8rem", background: savedBillId ? "#1d4ed8" : undefined }}
                  onClick={handlePrintBill}
                  disabled={!savedBillId}
                >
                  Print Bill
                </button>
              </div>
            </aside>
          );
        })()}
      </div>

      <footer className="clinical-footer">
        <div className="review-note">
          {savedBillId ? `Saved bill #${savedBillId}` : reviewMode ? "Review mode: inputs locked" : "Draft mode"}
        </div>
        <div className="footer-actions" data-export-ignore="true">
          {!savedBillId && (
            <button
              className="btn-action secondary"
              type="button"
              onClick={() => setReviewMode((prev) => !prev)}
            >
              {reviewMode ? "Edit" : "Review"}
            </button>
          )}
          <button className="btn-action secondary" type="button" onClick={handleExportPdf}>
            Export PDF
          </button>
          <button
            className="btn-action"
            type="button"
            onClick={handlePrintBill}
            disabled={!savedBillId}
          >
            Print
          </button>
        </div>
      </footer>
    </div>
  );
}
